// File: assets/src/js/app/i18n.ts

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpBackend from "i18next-http-backend";
import type { InitOptions } from "i18next";

// --- Configuration ---

// 1. Get language settings from WordPress (with fallbacks)
const langSettings = window.themeVariables?.language || {
  current: "tr", // Fallback current language
  default: "tr", // Fallback default language
  supported: ["tr", "en"], // Fallback supported languages
  names: { tr: "Türkçe", en: "English" }, // Fallback names
};

const supportedLngs = langSettings.supported;
const fallbackLng = langSettings.default;
const preferredLanguageCookie = "preferred_language"; // Your cookie name

// 2. Determine if running in development mode (using Vite's env variable)
const isDevelopment = import.meta.env.DEV;

// 3. Define base paths for translation files
// Development: Relative to the Vite dev server root (usually '/')
// Production: Relative to the domain root, pointing to the built assets
const devLoadPathBase =
  window.themeVariables?.themePath + "/assets/src/locales"; // Relative to Vite root (http://localhost:3000/locales/...)
const prodLoadPathBase =
  window.themeVariables?.themePath + "/assets/dist/assets/locales"; // Matches viteStaticCopy dest + theme path

const loadPathBase = isDevelopment ? devLoadPathBase : prodLoadPathBase;

// --- i18next Initialization ---

i18n
  // Load translations using http backend
  .use(HttpBackend)
  // Detect user language
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // Initialize i18next
  .init(
    {
      // --- Backend Settings ---
      backend: {
        // Path where resources get loaded from
        // {{lng}} = language code (e.g., 'en')
        // {{ns}} = namespace (default is 'translation')
        loadPath: `${loadPathBase}/{{lng}}/{{ns}}.json`,
      },

      // --- Language Detection Settings ---
      detection: {
        // Order and from where user language should be detected
        order: ["cookie", "navigator"], // Prioritize cookie, then browser language

        // Name of the cookie to check for language preference
        lookupCookie: preferredLanguageCookie,

        // Cache user language detection result in cookie
        caches: ["cookie"],

        // Options for language cookie
        cookieOptions: {
          path: "/", // Cookie available site-wide
          sameSite: "lax", // changed to lowercase
          // 'expires' and 'domain' can be set if needed, but defaults are often fine
        },

        // Only detect languages that are in supportedLngs
        checkSupportedLngs: true,
      },

      // --- Core i18next Settings ---
      // Language to use if translations in user language are not available
      fallbackLng: fallbackLng,

      // Array of allowed languages
      supportedLngs: supportedLngs,

      // Default namespace (matches filename 'translation.json')
      ns: ["translation"],
      defaultNS: "translation",

      // Enable debugging output in development mode
      debug: isDevelopment,

      // --- React Integration Settings ---
      react: {
        // Set to true if you want to use <Suspense> for loading translations
        useSuspense: false,
      },

      interpolation: {
        // React already safes from xss
        escapeValue: false,
      },
    } as InitOptions // cast so TS picks the correct overload
  )
  .then(() => {
    console.log("i18next initialized successfully.");
    // You could potentially dispatch an action here if needed after init
  })
  .catch((err) => {
    console.error("i18next initialization failed:", err);
  });

export default i18n;
